// Advanced Weight Loss Support (Dr. Mavroson's Concierge Weight Management
// Program) — /advanced-weight-loss. NOT a Beluga visit; $149/month membership (was $99 until 2026-10-11).
// Questions: Luma-style goals/eligibility + Fitpal's GLP-1 intake (prior GLP-1
// use, dose, side effects, gastric bypass, activity, diet). Shared steps and
// helpers in ./conciergeCommon.js. Copy from
// doctorskinnyshot.com/products/concierge-weight-management-program.
import {
  ageConfirmQuestion, stateQuestion, sexAtBirthQuestion, pregnancyQuestion, heightWeightQuestion,
  cancerQuestion, diabetesQuestion, conditionHistoryQuestion, conditionDetailsQuestion,
  medicationsQuestion, allergiesQuestion, bloodworkQuestion, additionalQuestionsQuestion, closingSteps,
  stateGate, applyCommonBranches, commonDisqualify, NOT_SERVED_REASON, flaggedConditions,
} from './conciergeCommon';

export const ADVANCED_WEIGHT_LOSS_PRICE = 149;
export const ADVANCED_WEIGHT_LOSS_PLAN_SLUG = 'advanced-weight-loss-99';
export const ADVANCED_WEIGHT_LOSS_NAME = 'Advanced Weight Loss Support';
export const ADVANCED_WEIGHT_LOSS_PRIMARY_GOAL = 'advancedWeightLoss';
export const WEIGHT_LOSS_DESCRIPTION =
  'A personalized, physician-guided membership for patients seeking comprehensive weight-management support and a more individualized approach to wellness. Members receive direct cell phone access to Dr. Mavroson, personalized treatment planning, and preferred member pricing on eligible services and advanced testing.';
export const WEIGHT_LOSS_INCLUDES = [
  'Direct cell phone access to Dr. Mavroson for text or calls',
  'Personalized weight-management planning and ongoing oversight',
  'Metabolic lab evaluation and review',
  'Preferred pricing on eligible compounded medications',
  'Member pricing on advanced hormone testing',
  'Member pricing on GI-Mapping, biological age testing (Epigenetics), microplastics testing, and other specialty lab services',
  'Preferred pricing on full-body MRI services',
  'Ongoing treatment management and clinical follow-up',
];
export const WEIGHT_LOSS_FOOTNOTE = 'Does not include the cost of medication. Offered exclusively in New York.';
export const WEIGHT_LOSS_IMAGE = '/programs/concierge-weight-management.jpg';

const introStep = {
  id: 'ap-intro', type: 'program-overview', question: 'Advanced Weight Loss Support',
  subtitle: 'Monthly membership · 100% online · Physician-guided weight management',
};
const weightLossGoalQuestion = {
  id: 'weight-loss-goal', type: 'single-select', question: 'How much weight would you like to lose?',
  options: [
    { label: '10 to 20 lbs', value: '10 to 20 lbs' }, { label: '21 to 50 lbs', value: '21 to 50 lbs' },
    { label: '51 to 100 lbs', value: '51 to 100 lbs' }, { label: 'Over 100 lbs', value: 'over 100 lbs' },
  ],
};
const goalWeightQuestion = {
  id: 'goal-weight', type: 'number', question: 'What is your goal weight?', subtitle: 'Enter your target weight in pounds.',
  placeholder: 'e.g., 165', inputType: 'number', unit: 'lbs', validation: { min: 60, max: 600 },
};
const weightLossReasonQuestion = {
  id: 'weight-loss-reason', type: 'single-select', question: 'What is your primary reason for taking weight loss seriously?',
  options: [
    { label: 'I want to live longer', value: 'live-longer' }, { label: 'I want to feel and look better', value: 'feel-look-better' },
    { label: 'I want to reduce current health issues', value: 'reduce-health-issues' }, { label: 'All of these', value: 'all' },
  ],
};
const diagnosisTimeQuestion = {
  id: 'diagnosis-time', type: 'single-select', question: 'When were you first told you were overweight or obese?',
  options: [
    { label: 'Within the last year', value: 'under-1-year' }, { label: '1–5 years ago', value: '1-5-years' },
    { label: 'More than 5 years ago', value: 'over-5-years' }, { label: "I haven't been told", value: 'never' },
  ],
};
const currentGlpQuestion = {
  id: 'current-glp-medications', type: 'multi-select',
  question: 'Are you currently, or have you in the past two months, taken any of the following medications?', subtitle: 'Select all that apply',
  options: [
    { label: 'Semaglutide (Ozempic®, Wegovy®, or compounded)', value: 'semaglutide' },
    { label: 'Tirzepatide (Mounjaro®, Zepbound®, or compounded)', value: 'tirzepatide' },
    { label: 'Another weight-loss medication', value: 'other' },
    { label: 'None of these', value: 'none' },
  ],
};
const currentDoseQuestion = {
  id: 'current-dose', type: 'single-select', question: 'Which dose most closely matches your most recent weekly dose?',
  options: [
    { label: 'Semaglutide 0.25 mg', value: 'semaglutide-0.25' }, { label: 'Semaglutide 0.5 mg', value: 'semaglutide-0.5' },
    { label: 'Semaglutide 1 mg', value: 'semaglutide-1' }, { label: 'Semaglutide 1.7 mg', value: 'semaglutide-1.7' },
    { label: 'Semaglutide 2.4 mg', value: 'semaglutide-2.4' },
    { label: 'Tirzepatide 2.5 mg', value: 'tirzepatide-2.5' }, { label: 'Tirzepatide 5 mg', value: 'tirzepatide-5' },
    { label: 'Tirzepatide 7.5 mg', value: 'tirzepatide-7.5' }, { label: 'Tirzepatide 10 mg', value: 'tirzepatide-10' },
    { label: 'Tirzepatide 12.5 mg', value: 'tirzepatide-12.5' }, { label: 'Tirzepatide 15 mg', value: 'tirzepatide-15' },
    { label: 'Other / not sure', value: 'other' },
  ],
};
const sideEffectsQuestion = {
  id: 'side-effects', type: 'multi-select', question: 'Have you experienced any of the following side effects from your current medication?', subtitle: 'Select all that apply',
  options: [
    { label: 'Nausea', value: 'nausea' }, { label: 'Vomiting', value: 'vomiting' }, { label: 'Constipation', value: 'constipation' },
    { label: 'Diarrhea', value: 'diarrhea' }, { label: 'Fatigue', value: 'fatigue' }, { label: 'Muscle loss', value: 'muscle-loss' },
    { label: 'Other', value: 'other' }, { label: 'None', value: 'none' },
  ],
};
const medicationProgressQuestion = {
  id: 'medication-progress', type: 'single-select', question: 'How are you responding to your medication?',
  options: [
    { label: "I'm losing weight and doing well", value: 'good' }, { label: "I'm losing weight but have side effects", value: 'side-effects' },
    { label: "I'm not losing weight", value: 'no-effect' }, { label: 'I stopped taking it', value: 'stopped' },
  ],
};
const gastricBypassQuestion = {
  id: 'gastric-bypass', type: 'single-select', question: 'Have you had gastric bypass or bariatric surgery in the past 6 months?',
  options: [{ label: 'Yes', value: 'yes', disqualifies: true }, { label: 'No', value: 'no' }],
};
const glpAllergyQuestion = {
  id: 'glp-allergy', type: 'single-select', question: 'Are you allergic to any GLP-1 medications (semaglutide, tirzepatide, liraglutide)?',
  options: [{ label: 'Yes', value: 'yes', disqualifies: true }, { label: 'No', value: 'no' }, { label: 'Not sure / never taken one', value: 'unknown' }],
};
const activityQuestion = {
  id: 'activity-level', type: 'single-select', question: 'What is your current physical activity level?',
  options: [
    { label: 'Sedentary (little or no exercise)', value: 'sedentary' }, { label: 'Light (1–2 days a week)', value: 'light' },
    { label: 'Moderate (3–4 days a week)', value: 'moderate' }, { label: 'Active (5+ days a week)', value: 'active' },
  ],
};
const dietQuestion = {
  id: 'dietary-plan', type: 'single-select', question: 'Are you currently following a specific dietary plan?',
  options: [
    { label: 'No specific plan', value: 'none' }, { label: 'Calorie counting / portion control', value: 'calorie' },
    { label: 'Low-carb or keto', value: 'low-carb' }, { label: 'Intermittent fasting', value: 'fasting' },
    { label: 'Plant-based', value: 'plant-based' }, { label: 'Other', value: 'other' },
  ],
};

export const advancedWeightLossFlow = [
  introStep, weightLossGoalQuestion, goalWeightQuestion, weightLossReasonQuestion, ageConfirmQuestion, stateQuestion,
  sexAtBirthQuestion, pregnancyQuestion, heightWeightQuestion, diagnosisTimeQuestion, cancerQuestion, diabetesQuestion,
  conditionHistoryQuestion, conditionDetailsQuestion, gastricBypassQuestion, glpAllergyQuestion,
  currentGlpQuestion, currentDoseQuestion, sideEffectsQuestion, medicationProgressQuestion,
  medicationsQuestion, allergiesQuestion, activityQuestion, dietQuestion, bloodworkQuestion, additionalQuestionsQuestion,
  ...closingSteps,
];
export const advancedWeightLossFlowById = advancedWeightLossFlow.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

export const buildDynamicAdvancedWeightLossFlow = (answers = {}) => {
  const gated = stateGate(advancedWeightLossFlow, answers);
  if (gated) return gated;
  let flow = applyCommonBranches([...advancedWeightLossFlow], answers);
  const glp = answers['current-glp-medications'];
  const onGlp = Array.isArray(glp) && glp.some((v) => v && v !== 'none');
  if (!onGlp) flow = flow.filter((q) => !['current-dose', 'side-effects', 'medication-progress'].includes(q.id));
  return flow;
};

// Hard stops: under 18, pregnant/breastfeeding, active cancer, type 1 diabetes
// (type 2 is flagged, not stopped — the program treats it), recent bariatric
// surgery, GLP-1 allergy.
export const isDisqualifyingAnswer = (questionId, answer) => {
  if (questionId === 'gastric-bypass' && answer === 'yes') {
    return { disqualified: true, reason: 'Patients who have had gastric bypass or bariatric surgery within the past 6 months are not eligible for this program yet. Please consult with your surgeon first.' };
  }
  if (questionId === 'glp-allergy' && answer === 'yes') {
    return { disqualified: true, reason: 'Due to your reported allergy to GLP-1 medications, you may not be a candidate for this program. Please consult with your healthcare provider.' };
  }
  const r = commonDisqualify(questionId, answer, { diabetesStops: ['type1'] });
  if (r.disqualified && questionId === 'diabetes') return { disqualified: true, reason: NOT_SERVED_REASON };
  return r;
};

export const weightLossFlags = (answers = {}) => {
  const flags = flaggedConditions(answers);
  const hw = answers['height-weight'];
  const bmi = hw && Number(hw.bmi);
  if (Number.isFinite(bmi) && bmi < 25) flags.push(`BMI ${bmi.toFixed(1)} (below 25)`);
  if (answers['medication-progress'] === 'side-effects') flags.push('Side effects on current GLP-1');
  return flags;
};

export default advancedWeightLossFlow;
